import {
  Badge,
  BadgeVariants,
  BadgeSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  TypographyElements,
} from '@carboledger/ui';
import { cx } from '@/lib/utils/cx';
import styles from './AuthCarouselVisuals.module.scss';

const STATUS_ROWS = [
  { id: 1, status: 'Received', tone: BadgeVariants.success, qty: '+4,545.66' },
  { id: 2, status: 'Forward (Inbound)', tone: BadgeVariants.primary, qty: '+200.45' },
  { id: 3, status: 'To receive', tone: BadgeVariants.warning, qty: '+4,545.66' },
  { id: 4, status: 'Write off', tone: BadgeVariants.error, qty: '-120' },
  { id: 5, status: 'Forward (Outbound)', tone: BadgeVariants.primary, qty: '-4,545.66' },
  { id: 6, status: 'Dispatched', tone: BadgeVariants.warning, qty: '-400.23' },
  { id: 7, status: 'Allocated', tone: BadgeVariants.error, qty: '-800.34' },
  { id: 8, status: 'Declaration generated', tone: BadgeVariants.success, qty: '-4,545.66' },
];

/** Slide 1 — the mass-balance table mockup. */
export const MassBalanceVisual = () => {
  return (
    <div className={styles.massBalanceGroup} aria-hidden="true">
      <div className={styles.tableCard}>
        <div className={styles.tableToolbar}>
          <Typography
            variant={TypographyVariants.caption}
            color={TypographyColors.primary}
            className={styles.pill}
          >
            04/03/2025 – 04/03/2026
          </Typography>
          <Typography
            variant={TypographyVariants.caption}
            color={TypographyColors.secondary}
            weight={TypographyWeights.semibold}
            className={styles.pillTag}
          >
            Filter
          </Typography>
          <div className={styles.toggleTabs}>
            <Typography
              variant={TypographyVariants.caption}
              color={TypographyColors.brand}
              weight={TypographyWeights.semibold}
              className={styles.toggleTabActive}
            >
              Detailed
            </Typography>
            <Typography
              variant={TypographyVariants.caption}
              color={TypographyColors.secondary}
              weight={TypographyWeights.semibold}
              className={styles.toggleTab}
            >
              Summary
            </Typography>
          </div>
        </div>
        <div className={styles.tableHeaderRow}>
          <Typography
            variant={TypographyVariants.caption}
            color={TypographyColors.secondary}
            weight={TypographyWeights.semibold}
            className={styles.colNo}
          >
            S.No.
          </Typography>
          <Typography
            variant={TypographyVariants.caption}
            color={TypographyColors.secondary}
            weight={TypographyWeights.semibold}
            className={styles.colStatus}
          >
            Status
          </Typography>
          <Typography
            variant={TypographyVariants.caption}
            color={TypographyColors.secondary}
            weight={TypographyWeights.semibold}
            className={styles.colDecl}
          >
            Declaration no.
          </Typography>
          <Typography
            variant={TypographyVariants.caption}
            color={TypographyColors.secondary}
            weight={TypographyWeights.semibold}
            className={styles.colQty}
          >
            Quantity (MT)
          </Typography>
        </div>
        {STATUS_ROWS.map((row) => (
          <div key={row.id} className={styles.tableRow}>
            <Typography
              variant={TypographyVariants.caption}
              color={TypographyColors.primary}
              className={styles.colNo}
            >
              {row.id}
            </Typography>
            <div className={styles.colStatus}>
              <Badge variant={row.tone} size={BadgeSizes.sm}>
                {row.status}
              </Badge>
            </div>
            <Typography
              variant={TypographyVariants.caption}
              color={TypographyColors.primary}
              className={styles.colDecl}
            >
              SD-4358294
            </Typography>
            <Typography
              variant={TypographyVariants.caption}
              color={TypographyColors.primary}
              className={styles.colQty}
            >
              {row.qty}
            </Typography>
          </div>
        ))}
      </div>

      <div className={styles.tabsFloat}>
        <Typography
          variant={TypographyVariants.caption}
          color={TypographyColors.onBrand}
          weight={TypographyWeights.semibold}
          className={styles.tabsFloatActive}
        >
          Material movement summary
        </Typography>
        {/* No TypographyColors token for $primary-200 — set on .tabsFloat and inherited. */}
        <Typography
          variant={TypographyVariants.caption}
          color={TypographyColors.inherit}
          weight={TypographyWeights.semibold}
        >
          Physical reconciliation summary
        </Typography>
        <Typography
          variant={TypographyVariants.caption}
          color={TypographyColors.inherit}
          weight={TypographyWeights.semibold}
        >
          Mass balance summary
        </Typography>
      </div>
      {/* Decoration only — the box is a plain <div> (not <Button>) so this
          non-interactive mockup never becomes a real (keyboard-focusable)
          control; the label inside it still goes through Typography. */}
      <div className={styles.inviteButton}>
        <Typography
          variant={TypographyVariants.caption}
          color={TypographyColors.onBrand}
          weight={TypographyWeights.semibold}
          as={TypographyElements.span}
        >
          + Invite auditors
        </Typography>
      </div>
      <div className={styles.exportButton}>
        <Typography
          variant={TypographyVariants.caption}
          color={TypographyColors.brand}
          weight={TypographyWeights.semibold}
          as={TypographyElements.span}
        >
          Export csv
        </Typography>
      </div>
    </div>
  );
};

/** Slide 2 — the declaration-extraction card pair. */
export const DeclarationVisual = () => {
  return (
    <div className={styles.declarationRow} aria-hidden="true">
      <div className={styles.pdfCard}>
        <div className={styles.pdfHeader}>
          <div className={styles.pdfBadge}>
            <Typography
              variant={TypographyVariants.caption}
              color={TypographyColors.onBrand}
              weight={TypographyWeights.semibold}
              as={TypographyElements.span}
            >
              PDF
            </Typography>
          </div>
          <Typography
            variant={TypographyVariants.label}
            color={TypographyColors.primary}
            className={styles.pdfTitle}
          >
            SD-243580346
          </Typography>
        </div>
        <div className={styles.skeletonBar} />
        <div className={styles.skeletonBar} />
        <div className={styles.skeletonBar} />
        <div className={styles.skeletonBarShort} />
        <div className={styles.skeletonBarMed} />
      </div>

      {/* $primary-200 (no TypographyColors token) comes from .extractArrow's
          own `color` — color={TypographyColors.inherit} picks that up via normal CSS
          inheritance rather than fighting it with a competing colour class. */}
      <div className={styles.extractArrow}>
        <Typography
          variant={TypographyVariants.caption}
          color={TypographyColors.inherit}
          weight={TypographyWeights.semibold}
          as={TypographyElements.span}
        >
          Auto-Extract
        </Typography>
        <Typography
          variant={TypographyVariants.body}
          color={TypographyColors.onBrand}
          as={TypographyElements.span}
          className={styles.arrowGlyph}
        >
          →
        </Typography>
        <Typography
          variant={TypographyVariants.caption}
          color={TypographyColors.inherit}
          weight={TypographyWeights.semibold}
          as={TypographyElements.span}
        >
          in 4 seconds
        </Typography>
      </div>

      <div className={styles.dataCard}>
        <Typography
          variant={TypographyVariants.label}
          color={TypographyColors.primary}
          className={styles.dataCardTitle}
        >
          SD-243580346
        </Typography>
        <div className={styles.divider} />
        {[
          ['Material', 'Biodiesel'],
          ['Quantity', '5,432.45 MT'],
          ['GHG savings', '82 %'],
          ['Origin', 'Italy'],
          ['Raw material', 'UCO'],
          ['GHG emissions', '24.50 gCO2 e / MJ'],
          ['Date of dispatch', '10/04/2026'],
          ['Feedstock', 'Annex IX A'],
        ].map(([label, value]) => (
          <div key={label} className={styles.dataRow}>
            <Typography
              variant={TypographyVariants.caption}
              color={TypographyColors.secondary}
              className={styles.dataLabel}
            >
              {label}
            </Typography>
            <Typography
              variant={TypographyVariants.caption}
              color={TypographyColors.primary}
              className={styles.dataValue}
            >
              {value}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

const STANDARD_CARDS = [
  {
    key: 'plus',
    title: 'ISCC PLUS',
    subtitle: 'Global · Bioeconomy',
    tags: ['Plastics', 'Chemicals', 'Food'],
    tone: 'brand',
  },
  {
    key: 'eu',
    title: 'ISCC EU',
    subtitle: 'Europe · RED II',
    tags: ['Biodiesel', 'HVO', 'Biogas'],
    tone: 'light',
    raised: true,
  },
  {
    key: 'corsia',
    title: 'ISCC CORSIA',
    subtitle: 'Aviation fuel',
    tags: ['SAF', 'LCAF'],
    tone: 'brand',
  },
];

/** Slide 3 — the three ISCC standard cards. */
export const StandardsVisual = () => {
  return (
    <div className={styles.standardsRow} aria-hidden="true">
      {STANDARD_CARDS.map((card) => (
        <div
          key={card.key}
          className={cx(
            styles.standardCard,
            card.tone === 'brand' && styles.standardCardBrand,
            card.raised && styles.standardCardRaised,
          )}
        >
          {/* color={TypographyColors.primary} is the base; `.standardCardBrand .standardTitle`
              (two classes, higher specificity than Typography's own single
              colour class) still overrides it to onBrand on the blue cards. */}
          <Typography
            variant={TypographyVariants.label}
            color={TypographyColors.primary}
            className={styles.standardTitle}
          >
            {card.title}
          </Typography>
          <Typography
            variant={TypographyVariants.caption}
            color={TypographyColors.secondary}
            className={styles.standardSubtitle}
          >
            {card.subtitle}
          </Typography>
          <div className={styles.divider} />
          <div className={styles.standardTags}>
            {card.tags.map((tag) => (
              <Typography
                key={tag}
                variant={TypographyVariants.caption}
                color={TypographyColors.secondary}
                weight={TypographyWeights.semibold}
                className={styles.standardTag}
              >
                {tag}
              </Typography>
            ))}
          </div>
          <Typography
            variant={TypographyVariants.label}
            color={TypographyColors.success}
            className={styles.standardLive}
          >
            ✓ Live
          </Typography>
        </div>
      ))}
    </div>
  );
};
